import {
  type MouseEvent as ReactMouseEvent,
  type TouchEvent as ReactTouchEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNotification, useTranslation } from '@refinedev/core';
import { IconCheck } from '@tabler/icons-react';
import {
  Alert,
  AspectRatio,
  Box,
  Button,
  Checkbox,
  Flex,
  Loader,
  Modal,
  MultiSelect,
  NumberInput,
  SegmentedControl,
  Stack,
  Text,
  Textarea,
  TextInput,
  useComputedColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useGenerateClaimInvoice, useUploadClaimInvoice } from 'api/action';
import { CLAIM_TARIFF_STATUS } from '@billing/validation/tariff';
import { validateBillingNumber } from '@billing/validation';
import {
  useCreateDebit,
  useGetClaim,
  useUpdateClaim,
  useUpdateDebit,
  useUpdateProvider,
} from 'api/resource';
import { useQueryClient } from '@tanstack/react-query';
import { type InvoiceType, invoiceTypeEnum } from '@billing/validation/invoice';
import { Claim, License, Payor, Tariff } from 'interfaces';
import * as Sentry from '@sentry/react';
import { useCurrentSiteStore } from 'store/SiteStore';
import { buttonTextColor } from 'utils/buttonUtils';
import {
  applyClaimTariffTotalsWithOverrides,
  sumTariffTotals,
} from 'utils/tariffUtils';
import {
  INVOICE_BUCKET,
  getInvoiceAttachmentBucketByFileKey,
  getLatestClaimInvoiceAttachmentKey,
} from 'utils/faxUtils';
import { uploadFileToS3 } from 'utils/fileUpload';
import { getBillingNumberValidationError } from 'utils/billingNumberValidationUtils';
import {
  ClaimFaxRecipientSelection,
  ClaimFaxRecipientSelector,
  SendFaxButton,
} from 'pages/claims/components';
import { ClaimInvoicePreview } from './ClaimInvoicePreview';
import {
  buildInvoiceDebitChanges,
  createUpdatedWCBLicense,
  GenerationMode,
  getDebitGenerationConfig,
  getWCBBillingLicense,
  upsertWCBLicense,
} from './claimInvoiceModalHelpers';

const DESKTOP_MODAL_HEIGHT = '82dvh';

const calculateMissingTariffTotals = (claim: Claim | null): Tariff[] => {
  const tariffs = (claim?.tariffs || []) as Tariff[];

  return claim && tariffs.some((tariff) => tariff.total == null)
    ? applyClaimTariffTotalsWithOverrides(claim)
    : tariffs;
};

interface ClaimInvoiceButtonProps {
  opened: boolean;
  close: () => void;
  selectedClaim: Claim | null;
  setSelectedClaim: (claim: Claim | null) => void;
  payors: Payor[];
}

export const ClaimInvoiceModal = ({
  opened,
  close,
  selectedClaim,
  setSelectedClaim,
  payors,
}: ClaimInvoiceButtonProps) => {
  const { id } = useCurrentSiteStore();
  const { open } = useNotification();
  const { translate } = useTranslation();
  const queryClient = useQueryClient();
  const theme = useMantineTheme();
  const isCompact = useMediaQuery('(max-width: 1200px)');
  const tariffs = calculateMissingTariffTotals(selectedClaim);
  const computedColorScheme = useComputedColorScheme('dark');
  const modalButtonTextColor =
    computedColorScheme === 'dark' ? 'white' : 'black';
  const textareaInputStyles = (stylesTheme: any) => ({
    input: {
      background:
        computedColorScheme === 'dark' ? stylesTheme.colors.dark[8] : undefined,
      borderColor:
        computedColorScheme === 'dark'
          ? stylesTheme.colors.dark[4]
          : stylesTheme.colors.gray[4],
    },
  });
  const modalHeight = isCompact ? 'auto' : DESKTOP_MODAL_HEIGHT;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isDraggingRef = useRef(false);
  const faxActionsRef = useRef<HTMLDivElement | null>(null);

  const initialTariffSum = sumTariffTotals(tariffs).toFixed(2);

  const [isLoading, setIsLoading] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [invoiceRate, setInvoiceRate] = useState<string | null>(null);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [internalComment, setInternalComment] = useState<string>('');
  const [serviceDescription, setServiceDescription] = useState<string>('');
  const [insuranceInfo, setInsuranceInfo] = useState<string>('');
  const [invoiceComments, setInvoiceComments] = useState<string>('');
  const [pendingWCBLicense, setPendingWCBLicense] = useState('');
  const [isSavingWCBLicense, setIsSavingWCBLicense] = useState(false);
  const [uploadedInvoiceFile, setUploadedInvoiceFile] = useState<File | null>(
    null
  );
  const [includeCoverLetter, setIncludeCoverLetter] = useState(false);
  const [generationMode, setGenerationMode] = useState<GenerationMode>(
    invoiceTypeEnum.site
  );
  const [includeWCBInvoice, setIncludeWCBInvoice] = useState(false);
  const [invoiceType, setInvoiceType] = useState<InvoiceType>(
    invoiceTypeEnum.site
  );

  const [invoiceAttachmentKey, setInvoiceAttachmentKey] = useState<
    string | null
  >(getLatestClaimInvoiceAttachmentKey(selectedClaim));
  const [shouldSendFax, setShouldSendFax] = useState(false);
  const [hasValidFaxRecipient, setHasValidFaxRecipient] = useState(false);
  const [faxRecipientValue, setFaxRecipientValue] = useState('');

  const [hasMarkedInCurrentPreview, setHasMarkedInCurrentPreview] =
    useState(false);

  const [selectedTariffIDs, setSelectedTariffIDs] = useState<string[]>(() =>
    tariffs.map((t) => t.id)
  );
  const [selectedTariffs, setSelectedTariffs] = useState<Tariff[]>([
    ...tariffs,
  ]);
  const [splitRatio, setSplitRatio] = useState(0.34);
  const [dragging, setDragging] = useState(false);
  const [isClaimPreviewExpanded, setIsClaimPreviewExpanded] = useState(true);

  const invoiceClaim = useGenerateClaimInvoice();
  const uploadClaimInvoice = useUploadClaimInvoice();
  const createDebit = useCreateDebit();
  const updateDebit = useUpdateDebit();
  const updateClaim = useUpdateClaim();
  const updateProvider = useUpdateProvider();
  const { refetch: refetchSelectedClaim } = useGetClaim({
    id: selectedClaim?.id || '',
    displayNotification: false,
  });
  const isWCBClaim = selectedClaim?.payor?.name === 'WCB';
  const isWCBMode = generationMode === 'wcb';
  const wcbPayor =
    payors.find((payor) => payor.id === selectedClaim?.payorID) ||
    payors.find((payor) => payor.name === 'WCB') ||
    null;
  const providerLicenses = selectedClaim?.provider?.licenses || [];
  const wcbBillingLicense = getWCBBillingLicense({
    licenses: providerLicenses,
    siteID: id,
    claimPayorID: selectedClaim?.payorID,
  });
  const currentWCBBillingNumberIssue =
    wcbPayor && wcbBillingLicense
      ? validateBillingNumber(wcbPayor, wcbBillingLicense.license)
      : null;
  const hasValidWCBBillingNumber =
    Boolean(wcbBillingLicense?.license?.trim()) &&
    !currentWCBBillingNumberIssue;
  const pendingWCBLicenseIssue = wcbPayor
    ? validateBillingNumber(wcbPayor, pendingWCBLicense)
    : null;
  const pendingWCBLicenseError = wcbPayor
    ? getBillingNumberValidationError(
        wcbPayor,
        pendingWCBLicenseIssue,
        translate
      )
    : undefined;

  const resetPreviewFlowState = (attachmentKey: string | null) => {
    setInvoiceAttachmentKey(attachmentKey);
    setShouldSendFax(false);
    setHasValidFaxRecipient(false);
    setFaxRecipientValue('');
    setHasMarkedInCurrentPreview(false);
  };

  const syncClaimState = (claim: Claim | null) => {
    const nextTariffs = calculateMissingTariffTotals(claim);
    setSelectedTariffIDs(nextTariffs.map((t) => t.id));
    setSelectedTariffs(nextTariffs);
    setInternalComment('');
    setIncludeCoverLetter(false);
    setGenerationMode(invoiceTypeEnum.site);
    setInvoiceType(invoiceTypeEnum.site);
    setInvoiceRate('0.00');
    setIncludeWCBInvoice(false);
    setUploadedInvoiceFile(null);
    resetPreviewFlowState(getLatestClaimInvoiceAttachmentKey(claim));
  };

  const existingInvoices = (selectedClaim?.invoices || []).filter((invoice) =>
    Boolean(invoice?.id || invoice?.file)
  );
  const activeInvoice = selectedClaim?.invoices?.[0] || null;
  const hasExistingInvoice = existingInvoices.length > 0;
  const existingInvoiceAmountTotal = existingInvoices.reduce(
    (total, invoice) => {
      const amount = Number(invoice?.amount);
      return Number.isFinite(amount) ? total + amount : total;
    },
    0
  );
  const hasExistingInvoiceAmount = existingInvoices.some((invoice) =>
    Number.isFinite(Number(invoice?.amount))
  );

  const buildSubmittedClaimUpdate = (claim: Claim): Claim => {
    const shouldSubmitAllTariffs =
      generationMode === invoiceTypeEnum.site || !selectedTariffIDs.length;
    const selectedTariffIDSet = new Set(selectedTariffIDs);

    return {
      ...claim,
      tariffs: (claim.tariffs || []).map((tariff) => {
        const shouldSubmitTariff =
          shouldSubmitAllTariffs || selectedTariffIDSet.has(tariff.id);

        if (!shouldSubmitTariff) {
          return tariff;
        }

        return {
          ...tariff,
          status: CLAIM_TARIFF_STATUS.SUBMITTED,
        };
      }),
      internalComments: internalComment,
    };
  };

  useEffect(() => {
    setPendingWCBLicense(wcbBillingLicense?.license || '');
  }, [wcbBillingLicense?.license, selectedClaim?.id]);

  useEffect(() => {
    syncClaimState(selectedClaim);
  }, [selectedClaim?.id]);

  useEffect(() => {
    if (isCompact) {
      setIsClaimPreviewExpanded(true);
    }
  }, [isCompact, selectedClaim?.id, opened]);

  useEffect(() => {
    if (isCompact || !opened) return;

    const handleMove = (event: MouseEvent | TouchEvent) => {
      if (!isDraggingRef.current || !containerRef.current) return;
      if ('touches' in event) event.preventDefault();
      const rect = containerRef.current.getBoundingClientRect();
      const clientX =
        'touches' in event ? event.touches[0]?.clientX : event.clientX;
      if (clientX == null) return;
      const next = Math.min(
        0.55,
        Math.max(0.25, (clientX - rect.left) / rect.width)
      );
      setSplitRatio(next);
    };

    const handleStop = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      setDragging(false);
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleStop);
    window.addEventListener('touchmove', handleMove, { passive: false });
    window.addEventListener('touchend', handleStop);

    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleStop);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleStop);
    };
  }, [isCompact, opened]);

  useEffect(() => {
    if (!dragging) return;
    const previousCursor = document.body.style.cursor;
    document.body.style.cursor = 'col-resize';
    return () => {
      document.body.style.cursor = previousCursor;
    };
  }, [dragging]);

  useEffect(() => {
    if (!shouldSendFax) return;

    const frame = requestAnimationFrame(() => {
      faxActionsRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
        inline: 'nearest',
      });
    });

    return () => cancelAnimationFrame(frame);
  }, [shouldSendFax, hasValidFaxRecipient, faxRecipientValue]);

  const invoiceAttachmentBucket = useMemo(
    () => getInvoiceAttachmentBucketByFileKey(invoiceAttachmentKey),
    [invoiceAttachmentKey]
  );
  const showClaimPreview = !fileUrl;
  const invalidateClaimQueries = async () => {
    await queryClient.invalidateQueries({
      queryKey: ['claim'],
    });
  };

  const refreshAndGetLatestInvoiceAttachmentKey = async (): Promise<
    string | null
  > => {
    if (!selectedClaim?.id) {
      return getLatestClaimInvoiceAttachmentKey(selectedClaim);
    }

    try {
      await invalidateClaimQueries();
      const refreshedClaimResult = await refetchSelectedClaim();
      const refreshedClaim = refreshedClaimResult.data;

      if (refreshedClaim) {
        setSelectedClaim(refreshedClaim);
        return getLatestClaimInvoiceAttachmentKey(refreshedClaim);
      }
    } catch (error) {
      Sentry.captureException(error);
    }

    return getLatestClaimInvoiceAttachmentKey(selectedClaim);
  };

  const handleInvoiceClaim = async () => {
    if (!isWCBMode && !invoiceRate) {
      setIsLoading(false);
      setInvoiceError(
        translate(
          'pages:claims.invoiceModal.errors.invalidInvoiceRate',
          'Please enter a valid invoice rate.'
        )
      );
      return;
    }

    if (!selectedClaim?.id) {
      return;
    }

    setIsLoading(true);
    setInvoiceError(null);
    try {
      const resolvedInvoiceType = isWCBMode
        ? invoiceTypeEnum.tariff
        : invoiceType;

      const data = {
        claimID: selectedClaim.id,
        siteID: id,
        invoiceRate: Number(invoiceRate || 0),
        invoiceType: resolvedInvoiceType,
        selectedTariffIDs: selectedTariffIDs,
        serviceDescription,
        insuranceInfo,
        invoiceComments,
        includeCoverLetter,
        includeWCBForm: isWCBMode,
        includeWCBInvoice: isWCBMode ? includeWCBInvoice : undefined,
        replaceInvoiceID: activeInvoice?.id,
      };
      const result = await invoiceClaim.mutateAsync(data);
      if (result?.url) {
        setFileUrl(result.url);
        const latestInvoiceAttachmentKey =
          await refreshAndGetLatestInvoiceAttachmentKey();
        resetPreviewFlowState(latestInvoiceAttachmentKey);
      }
      if (isWCBMode) {
        setInvoiceType(invoiceTypeEnum.tariff);
      }
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setInvoiceError(null);
    setInvoiceRate('0');
    setFileUrl(null);
    setGenerationMode(invoiceTypeEnum.site);
    setInvoiceType(invoiceTypeEnum.site);
    setSelectedTariffIDs([]);
    setSelectedTariffs([]);
    setIsClaimPreviewExpanded(true);
    setInternalComment('');
    setServiceDescription('');
    setInsuranceInfo('');
    setInvoiceComments('');
    setIncludeCoverLetter(false);
    setIncludeWCBInvoice(false);
    setUploadedInvoiceFile(null);
    resetPreviewFlowState(null);
    setSelectedClaim(null);
    close();
  };

  const handleChipChange = (val: string) => {
    const nextMode = val as GenerationMode;
    setGenerationMode(nextMode);

    if (nextMode === 'wcb') {
      setInvoiceType(invoiceTypeEnum.tariff);
      setInvoiceRate(initialTariffSum);
      setSelectedTariffIDs(tariffs.map((t) => t.id));
      setSelectedTariffs([...tariffs]);
      setIncludeCoverLetter(false);
      setIncludeWCBInvoice(false);
      setUploadedInvoiceFile(null);
      setInvoiceError(null);
      return;
    }

    setIncludeWCBInvoice(false);
    setInvoiceType(nextMode);
    setInvoiceRate(
      nextMode === invoiceTypeEnum.tariff ? initialTariffSum : '0.00'
    );
    const nextTariffIDs =
      nextMode === invoiceTypeEnum.tariff ? tariffs.map((t) => t.id) : [];
    setSelectedTariffIDs(nextTariffIDs);
    setSelectedTariffs(
      nextMode === invoiceTypeEnum.tariff ? [...tariffs] : ([] as Tariff[])
    );
  };

  const handleDebitGeneration = async (
    config = getDebitGenerationConfig({
      isWCBMode,
      invoiceType,
      selectedTariffs,
      invoiceRate,
      fallbackTariffID: selectedClaim?.tariffs?.[0]?.id || '',
    })
  ) => {
    if (!selectedClaim) return;
    const debitChanges = buildInvoiceDebitChanges({
      claim: selectedClaim,
      config,
    });

    await Promise.all([
      ...debitChanges.create.map((debit) =>
        createDebit.mutateAsync(debit).catch((error) => {
          open({
            type: 'error',
            message: translate(
              'pages:claims.invoiceModal.errors.createDebitMessage',
              'Error creating debit for claim.'
            ),
            description: translate(
              'pages:claims.invoiceModal.errors.createDebitDescription',
              'There was an error creating debit(s) for the selected claim'
            ),
          });
          Sentry.captureException(error);
        })
      ),
      ...debitChanges.update.map((debit) =>
        updateDebit.mutateAsync(debit).catch((error) => {
          open({
            type: 'error',
            message: translate(
              'pages:claims.invoiceModal.errors.updateDebitMessage',
              'Error updating debit for claim.'
            ),
            description: translate(
              'pages:claims.invoiceModal.errors.updateDebitDescription',
              'There was an error updating debit(s) for the selected claim'
            ),
          });
          Sentry.captureException(error);
        })
      ),
    ]);
  };

  const handleUploadInvoice = async () => {
    if (!selectedClaim?.id || !uploadedInvoiceFile) {
      return;
    }

    const resolvedInvoiceType = isWCBMode
      ? invoiceTypeEnum.tariff
      : invoiceType;
    const amount = Number(invoiceRate || 0);

    if (Number.isNaN(amount) || amount <= 0) {
      setInvoiceError(
        translate(
          'pages:claims.invoiceModal.errors.invalidInvoiceRate',
          'Please enter a valid invoice rate.'
        )
      );
      return;
    }

    setIsLoading(true);
    setInvoiceError(null);

    try {
      const sanitizedName = uploadedInvoiceFile.name.replace(
        /[^a-zA-Z0-9._-]/g,
        '-'
      );
      const uploadKey = `invoices/${
        selectedClaim.id
      }-${Date.now()}-${sanitizedName}`;
      const [uploadedKey] = await uploadFileToS3(
        [uploadedInvoiceFile as any],
        INVOICE_BUCKET,
        [uploadKey]
      );

      if (!uploadedKey) {
        throw new Error('Invoice PDF upload failed');
      }

      await uploadClaimInvoice.mutateAsync({
        claimID: selectedClaim.id,
        siteID: id,
        uploadedFileKey: uploadedKey,
        amount,
        invoiceType: resolvedInvoiceType,
        selectedTariffIDs,
        internalComment,
        replaceInvoiceID: activeInvoice?.id,
      });

      await handleDebitGeneration(
        getDebitGenerationConfig({
          isWCBMode,
          invoiceType: resolvedInvoiceType,
          selectedTariffs,
          invoiceRate,
          fallbackTariffID: selectedClaim?.tariffs?.[0]?.id || '',
        })
      );

      await refreshAndGetLatestInvoiceAttachmentKey();
      handleCloseModal();
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setIsLoading(false);
    }
  };

  const markClaimInvoiced = async (
    closeOnSuccess: boolean
  ): Promise<boolean> => {
    if (!selectedClaim) return false;

    if (hasMarkedInCurrentPreview) {
      if (closeOnSuccess) {
        handleCloseModal();
      }
      return true;
    }

    setIsUpdatingStatus(true);
    try {
      await Promise.all([
        handleDebitGeneration(),
        updateClaim.mutateAsync({
          newData: buildSubmittedClaimUpdate(selectedClaim),
          dirtyFields: {
            tariffs: true,
            internalComments: Boolean(internalComment.trim()),
          },
        }),
      ]);
      setHasMarkedInCurrentPreview(true);
      if (closeOnSuccess) {
        handleCloseModal();
      }
      return true;
    } catch (error) {
      Sentry.captureException(error);
      return false;
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleMarkInvoiced = async () => {
    await markClaimInvoiced(true);
  };

  const shouldShowFaxSubmitButton = shouldSendFax;
  const hasFaxAttachmentContext =
    Boolean(selectedClaim?.id) &&
    Boolean(invoiceAttachmentBucket?.trim()) &&
    Boolean(invoiceAttachmentKey?.trim());

  const handleFaxRecipientChange = ({
    isValid,
    recipientValue,
  }: ClaimFaxRecipientSelection) => {
    setHasValidFaxRecipient(isValid);
    setFaxRecipientValue(recipientValue);
  };

  const handleDividerStart = (
    event: ReactMouseEvent<HTMLDivElement> | ReactTouchEvent<HTMLDivElement>
  ) => {
    if (isCompact) return;
    event.preventDefault();
    isDraggingRef.current = true;
    setDragging(true);
  };

  const handleSaveWCBLicense = async () => {
    const nextWCBLicense = pendingWCBLicense.trim();
    const provider = selectedClaim?.provider;
    if (
      !provider ||
      !selectedClaim?.id ||
      !nextWCBLicense ||
      pendingWCBLicenseIssue
    ) {
      return;
    }

    setIsSavingWCBLicense(true);
    try {
      const licenses = provider.licenses || [];
      const updatedLicense: Omit<License, 'payor'> = createUpdatedWCBLicense({
        claimPayorID: selectedClaim.payorID,
        wcbPayorID: wcbPayor?.id,
        nextWCBLicense,
        siteID: id,
      });
      const nextLicenses = upsertWCBLicense({
        licenses: licenses as Array<Omit<License, 'payor'>>,
        updatedLicense,
        siteID: id,
        claimPayorID: selectedClaim.payorID,
        wcbPayorID: wcbPayor?.id,
      });

      await updateProvider.mutateAsync({
        ...provider,
        siteID: id,
        licenses: nextLicenses,
      });

      await queryClient.invalidateQueries();

      const refreshedClaimResult = await refetchSelectedClaim();
      const refreshedClaim = refreshedClaimResult.data;

      if (refreshedClaim) {
        setSelectedClaim(refreshedClaim);
      }
    } catch (error) {
      Sentry.captureException(error);
      open({
        type: 'error',
        message: translate(
          'pages:claims.invoiceModal.errors.WCBBillingNumberUpdateFailed',
          'Could not update WCB billing number. Please try again.'
        ),
      });
    } finally {
      setIsSavingWCBLicense(false);
    }
  };

  const TariffSection = () => {
    if (tariffs?.length === 0) {
      return (
        <TextInput
          label={translate(
            'pages:claims.invoiceModal.tariffRateLabel',
            'Tariff Rate'
          )}
          disabled
          value={translate(
            'pages:claims.invoiceModal.noTariffsAvailable',
            'No tariffs available'
          )}
        />
      );
    }

    const options = tariffs?.map((t) => ({
      value: t.id,
      label: translate(
        'pages:claims.invoiceModal.tariffOptionLabel',
        {
          code: `${t.prefix}${t.code}`,
          rate: t.total?.toFixed(2),
        },
        `${t.prefix}${t.code} — $${t.total?.toFixed(2)}`
      ),
    }));
    return (
      <MultiSelect
        label={translate(
          'pages:claims.invoiceModal.tariffRateLabel',
          'Tariff Rate'
        )}
        data={options}
        w="95%"
        placeholder={
          selectedTariffIDs.length < tariffs.length
            ? translate(
                'pages:claims.invoiceModal.selectTariffsPlaceholder',
                'Select Tariffs'
              )
            : ''
        }
        value={selectedTariffIDs}
        onChange={(ids) => {
          setSelectedTariffIDs(ids);
          const nextTariffs = tariffs?.filter((t) => ids.includes(t.id)) || [];
          setSelectedTariffs(nextTariffs);
          setInvoiceRate(sumTariffTotals(nextTariffs).toFixed(2));
        }}
      />
    );
  };

  const renderPreview = () => (
    <Stack gap="xs">
      <AspectRatio ratio={2 / 1}>
        <iframe
          src={`${fileUrl}#navpanes=0&scrollbar=0`}
          title={translate(
            'pages:claims.invoiceModal.claimInvoiceTitle',
            'Claim Invoice'
          )}
          style={{
            flex: 1,
            border: 'none',
            width: '100%',
          }}
        />
      </AspectRatio>
      <Flex justify="center" gap="md">
        <Textarea
          autosize
          minRows={3}
          maxRows={5}
          w="55%"
          placeholder={translate(
            'pages:claims.invoiceModal.internalCommentPlaceholder',
            'Enter your internal comment here...'
          )}
          value={internalComment}
          onChange={(e) => setInternalComment(e.currentTarget.value)}
          styles={textareaInputStyles}
        />
      </Flex>
      <Flex justify="center" gap="md">
        <Flex direction="column" gap="sm" w="55%" p="md">
          <Checkbox
            label={translate(
              'pages:claims.invoiceModal.faxOptionalLabel',
              'Fax (optional)'
            )}
            checked={shouldSendFax}
            disabled={isUpdatingStatus}
            onChange={(event) => setShouldSendFax(event.currentTarget.checked)}
            data-testid="fax-options-toggle"
          />
          {shouldSendFax && (
            <ClaimFaxRecipientSelector
              payors={payors}
              claimPayorID={selectedClaim?.payorID}
              disabled={isUpdatingStatus}
              onChange={handleFaxRecipientChange}
            />
          )}
        </Flex>
      </Flex>
      <Flex justify="center" gap="md" ref={faxActionsRef}>
        <Button
          variant="outline"
          radius="md"
          color="gray"
          c={buttonTextColor(computedColorScheme, false)}
          onClick={() => setFileUrl(null)}
        >
          {translate('buttons.cancel', 'Cancel')}
        </Button>
        {shouldShowFaxSubmitButton ? (
          <SendFaxButton
            attachmentBucket={invoiceAttachmentBucket}
            attachmentKey={invoiceAttachmentKey || ''}
            claimID={selectedClaim?.id}
            recipient={faxRecipientValue}
            sendButtonLabel={translate(
              'pages:claims.invoiceModal.markInvoicedAndSendFaxButton',
              'Mark Invoiced & Send Fax'
            )}
            disabled={
              isUpdatingStatus ||
              !hasFaxAttachmentContext ||
              !hasValidFaxRecipient
            }
            onBeforeSend={() => markClaimInvoiced(false)}
            onSuccess={handleCloseModal}
          />
        ) : (
          <Button
            variant="outline"
            radius="md"
            color="green"
            c={buttonTextColor(computedColorScheme, false)}
            data-testid="mark-invoiced-primary"
            onClick={handleMarkInvoiced}
            loading={isUpdatingStatus}
            leftSection={<IconCheck size={16} />}
          >
            {translate(
              'pages:claims.invoiceModal.markInvoicedButton',
              'Mark Submitted'
            )}
          </Button>
        )}
      </Flex>
    </Stack>
  );

  const showInvoiceConfiguration = !isWCBMode || includeWCBInvoice;
  const primaryGenerateButtonLabel = isWCBMode
    ? includeWCBInvoice
      ? translate(
          'pages:claims.invoiceModal.generateWCBFormWithInvoiceButton',
          'Generate WCB Form + Invoice'
        )
      : translate(
          'pages:claims.invoiceModal.generateWCBFormOnlyButton',
          'Generate WCB Form'
        )
    : translate(
        'pages:claims.invoiceModal.generateInvoiceButton',
        {
          amount: Number(invoiceRate).toFixed(2),
        },
        `Generate $${Number(invoiceRate).toFixed(2)} Invoice`
      );
  const uploadButtonLabel = hasExistingInvoice
    ? translate(
        'pages:claims.invoiceModal.replaceUploadedInvoiceButton',
        'Upload PDF & Replace Invoice'
      )
    : translate(
        'pages:claims.invoiceModal.uploadInvoiceButton',
        'Upload PDF & Mark Submitted'
      );
  const shouldUploadInvoice = Boolean(uploadedInvoiceFile);
  const primaryActionLabel = shouldUploadInvoice
    ? uploadButtonLabel
    : primaryGenerateButtonLabel;
  const handlePrimaryAction = shouldUploadInvoice
    ? handleUploadInvoice
    : handleInvoiceClaim;

  const renderForm = () => (
    <Stack gap="sm">
      {hasExistingInvoice && (
        <Alert
          color="yellow"
          variant="light"
          title={translate(
            'pages:claims.invoiceModal.existingInvoiceAlertTitle',
            'Replacing existing invoice'
          )}
        >
          {translate(
            'pages:claims.invoiceModal.errors.invoiceExistsMessage',
            'The current invoice for this claim will be replaced.'
          )}
          {activeInvoice?.invoiceNumber != null ? (
            <div>
              {translate(
                'pages:claims.invoiceModal.replacingInvoiceNumber',
                { invoiceNumber: activeInvoice.invoiceNumber },
                `Replacing invoice #${activeInvoice.invoiceNumber}.`
              )}
            </div>
          ) : null}
          {hasExistingInvoiceAmount && (
            <div>
              {translate(
                'pages:claims.invoiceModal.existingInvoiceTotalAmount',
                { amount: existingInvoiceAmountTotal.toFixed(2) },
                `Existing invoice total: $${existingInvoiceAmountTotal.toFixed(
                  2
                )}`
              )}
            </div>
          )}
        </Alert>
      )}
      <Flex gap="xs" w="100%" justify="center">
        <SegmentedControl
          value={generationMode}
          data={[
            {
              label: translate(
                'pages:claims.invoiceModal.customInvoiceAmount',
                'Custom Invoice Amount'
              ),
              value: invoiceTypeEnum.site,
            },
            {
              label: translate(
                'pages:claims.invoiceModal.invoiceAmountByTariffs',
                'Invoice Amount by Tariffs'
              ),
              value: invoiceTypeEnum.tariff,
            },
            ...(isWCBClaim
              ? [
                  {
                    label: 'WCB Form',
                    value: 'wcb',
                  },
                ]
              : []),
          ]}
          onChange={handleChipChange}
        />
      </Flex>
      {isWCBClaim && isWCBMode && (
        <Flex w="95%" justify="center">
          <Checkbox
            label={translate(
              'pages:claims.invoiceModal.includeInvoiceWithWCBFormLabel',
              'Include invoice with WCB form'
            )}
            checked={includeWCBInvoice}
            onChange={(event) =>
              setIncludeWCBInvoice(event.currentTarget.checked)
            }
          />
        </Flex>
      )}
      {showInvoiceConfiguration ? (
        <Flex direction="column" align="center" gap="md" justify={'center'}>
          {invoiceType === invoiceTypeEnum.tariff ? (
            <TariffSection />
          ) : (
            <NumberInput
              label={translate(
                'pages:claims.invoiceModal.invoiceRateLabel',
                'Invoice Rate'
              )}
              error={invoiceError}
              onChange={(value) =>
                setInvoiceRate(value ? value.toString() : '0')
              }
              value={invoiceRate}
              prefix="$"
              placeholder={translate(
                'pages:claims.invoiceModal.invoiceRatePlaceholder',
                'Enter invoice rate'
              )}
              w="95%"
              m="md"
              disabled={isWCBMode}
            />
          )}
        </Flex>
      ) : null}
      <Flex direction="column" align="center" gap="md">
        <Textarea
          autosize
          minRows={3}
          w="95%"
          placeholder={translate(
            'pages:claims.invoiceModal.serviceDescriptionPlaceholder',
            'Service Description (optional)'
          )}
          value={serviceDescription}
          onChange={(e) => setServiceDescription(e.currentTarget.value)}
          disabled={!showInvoiceConfiguration}
          styles={textareaInputStyles}
        />
        <Textarea
          autosize
          minRows={3}
          w="95%"
          placeholder={translate(
            'pages:claims.invoiceModal.insuranceInformationPlaceholder',
            'Insurance Information (optional)'
          )}
          value={insuranceInfo}
          onChange={(e) => setInsuranceInfo(e.currentTarget.value)}
          disabled={!showInvoiceConfiguration}
          styles={textareaInputStyles}
        />
        <Textarea
          autosize
          minRows={3}
          w="95%"
          placeholder={translate(
            'pages:claims.invoiceModal.invoiceCommentsPlaceholder',
            'Invoice Comments (optional)'
          )}
          value={invoiceComments}
          onChange={(e) => setInvoiceComments(e.currentTarget.value)}
          disabled={!showInvoiceConfiguration}
          styles={textareaInputStyles}
        />
      </Flex>
      <Flex w="95%" justify="flex-start" ml={'md'}>
        <Checkbox
          ml={'md'}
          label={translate(
            'pages:claims.invoiceModal.includeCoverLetterLabel',
            'Include cover letter'
          )}
          checked={includeCoverLetter}
          onChange={(event) =>
            setIncludeCoverLetter(event.currentTarget.checked)
          }
        />
      </Flex>
      {isWCBClaim && isWCBMode && !includeWCBInvoice && (
        <Flex w="95%" justify="center">
          <Text c="dimmed" size="sm">
            {translate(
              'pages:claims.invoiceModal.wcbFormOnlyHint',
              'WCB form only mode. Turn on "Include invoice with WCB form" to configure invoice details.'
            )}
          </Text>
        </Flex>
      )}
      {showInvoiceConfiguration ? (
        <Flex direction="column" align="center" gap="xs" w="100%">
          <Text size="sm" w="95%">
            {translate(
              'pages:claims.invoiceModal.uploadPdfLabel',
              'Upload Invoice PDF'
            )}
          </Text>
          <Box w="95%">
            <input
              accept="application/pdf"
              data-testid="invoice-pdf-upload"
              type="file"
              onChange={(event) =>
                setUploadedInvoiceFile(event.currentTarget.files?.[0] || null)
              }
            />
          </Box>
          {uploadedInvoiceFile ? (
            <Text c="dimmed" size="sm">
              {uploadedInvoiceFile.name}
            </Text>
          ) : null}
        </Flex>
      ) : null}
      <Flex w="100%" justify="center" gap="lg">
        <Button
          onClick={handleCloseModal}
          variant="outline"
          c={modalButtonTextColor}
          color="gray"
        >
          {translate('buttons.cancel', 'Cancel')}
        </Button>
        <Button
          onClick={handlePrimaryAction}
          disabled={
            shouldUploadInvoice
              ? !showInvoiceConfiguration || Number(invoiceRate) <= 0
              : !isWCBMode && Number(invoiceRate) <= 0
          }
          c={modalButtonTextColor}
          color={shouldUploadInvoice ? 'teal' : 'blue'}
          loading={isLoading}
        >
          {primaryActionLabel}
        </Button>
      </Flex>
    </Stack>
  );

  return (
    <Modal
      opened={opened}
      onClose={handleCloseModal}
      title={
        hasExistingInvoice
          ? translate(
              'pages:claims.invoiceModal.replaceTitle',
              'Replace Invoice'
            )
          : translate('pages:claims.invoiceModal.title', 'Generate Invoice')
      }
      size="100%"
      centered
      closeOnClickOutside={false}
      trapFocus
      overlayProps={{ backgroundOpacity: 0.45, blur: 4 }}
    >
      {isLoading ? (
        <Flex justify="center" align="center" gap="xs" mih={320}>
          <Loader size={50} />
        </Flex>
      ) : (
        <Flex
          direction={isCompact ? 'column' : 'row'}
          gap="md"
          align="stretch"
          style={{
            height: modalHeight,
            minHeight: modalHeight,
            userSelect: dragging ? 'none' : 'auto',
            overflow: isCompact ? 'visible' : 'hidden',
          }}
          ref={containerRef}
        >
          {showClaimPreview && (
            <Box
              style={{
                flexGrow: isCompact ? 0 : splitRatio,
                flexBasis: isCompact ? 'auto' : 0,
                width: isCompact ? '100%' : undefined,
                maxWidth: isCompact ? '100%' : undefined,
                minWidth: isCompact ? '100%' : 280,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <ClaimInvoicePreview
                isCompact={isCompact}
                claim={
                  selectedClaim
                    ? {
                        ...selectedClaim,
                        tariffs: tariffs as Claim['tariffs'],
                      }
                    : null
                }
                selectedTariffIDs={selectedTariffIDs}
                hasExistingInvoiceAmount={hasExistingInvoiceAmount}
                existingInvoiceAmountTotal={existingInvoiceAmountTotal}
                expanded={isCompact ? isClaimPreviewExpanded : true}
                showWCBLicenseEditor={isWCBClaim && !hasValidWCBBillingNumber}
                wcbLicenseInputValue={pendingWCBLicense}
                wcbLicenseInputError={pendingWCBLicenseError}
                wcbLicenseInputMinLength={
                  wcbPayor?.billingNumberMinLength ?? undefined
                }
                wcbLicenseInputMaxLength={
                  wcbPayor?.billingNumberMaxLength ?? undefined
                }
                wcbLicenseNumericOnly={
                  wcbPayor?.billingNumberFormat === 'NUMERIC'
                }
                onWCBLicenseInputChange={setPendingWCBLicense}
                onSaveWCBLicense={handleSaveWCBLicense}
                isSavingWCBLicense={isSavingWCBLicense}
                onToggle={() => {
                  if (!isCompact) return;
                  setIsClaimPreviewExpanded((current) => !current);
                }}
              />
            </Box>
          )}

          {showClaimPreview && !isCompact && (
            <Box
              onMouseDown={handleDividerStart}
              onTouchStart={handleDividerStart}
              onDoubleClick={() => setSplitRatio(0.34)}
              role="separator"
              aria-label="Resize panels"
              style={{
                width: 1,
                cursor: 'col-resize',
                borderRadius: 8,
                background: dragging
                  ? theme.colors.blue[2]
                  : theme.colors.gray[2],
                border: `1px solid ${
                  dragging ? theme.colors.blue[4] : theme.colors.gray[4]
                }`,
              }}
            />
          )}

          <Box
            style={{
              flexGrow: isCompact || !showClaimPreview ? 0 : 1 - splitRatio,
              flexBasis: isCompact || !showClaimPreview ? 'auto' : 0,
              width: !showClaimPreview ? '100%' : undefined,
              minWidth: 0,
              minHeight: 0,
              border: '1px solid var(--mantine-color-gray-3)',
              borderRadius: 8,
              padding: 12,
              overflow: isCompact ? 'visible' : 'auto',
            }}
          >
            {fileUrl ? renderPreview() : renderForm()}
          </Box>
        </Flex>
      )}
    </Modal>
  );
};
